import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Input,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextareaAutosize,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { inputStyleObj } from "./WriteOff";
import { acctMonthToLabel, useCrossAllocation, getTodaysDateInLocalTimezone } from "./hooks";
import SettingsTwoToneIcon from "@mui/icons-material/SettingsTwoTone";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import UpdateGenericWorkflowDialog from "../Workflow/UpdateGenericWorkflowDialog";
import { customDateFormat } from "./utils";
import { numberFormatter } from "../../utils/generalFunctions";
import { toast, ToastContainer } from "react-toastify";

const History = ({ fixedDetails, history }) => {
  const [
    openUpdateGenericCrossAllocationWorkflowDialog,
    setOpenUpdateGenericCrossAllocationWorkflowDialog,
  ] = useState(false);
  const [currentWorkflowName, setCurrentWorkflowName] = useState("");
  const [currentTxnWorkflowDetails, setCurrentTxnWorkflowDetails] = useState(
    {}
  );

  const handleUpdateGenericWorkflowDialog = () => {
    setOpenUpdateGenericCrossAllocationWorkflowDialog(false);
  };
  return (
    <div className="cash-allocation-dialogue-container">
      <Grid container>
        {fixedDetails.map(({ label, value }, i) => (
          <Grid
            item
            xs={12}
            md={6}
            key={label}
            paddingLeft={i % 2 ? 2 : 0}
            paddingRight={i % 2 ? 0 : 2}
            marginBottom={2}
          >
            <span style={{ fontWeight: label === "Policy#" ? 800 : 400 }}>
              {label}
            </span>
            <span style={{ fontWeight: "bold" }}>: {value}</span>
          </Grid>
        ))}
      </Grid>
      <TableContainer sx={{ height: 275, overflow: "auto" }}>
        <Table>
          <TableHead className="table-head">
            <TableRow>
              <TableCell
                className="table-cell"
                sx={{ fontSize: "15px", fontWeight: 800 }}
              >
                New Allocation Date
              </TableCell>
              <TableCell
                className="table-cell"
                sx={{ fontSize: "15px", fontWeight: 800 }}
              >
                Changes
              </TableCell>
              <TableCell
                className="table-cell"
                sx={{ fontSize: "15px", fontWeight: 800 }}
              >
                Impact
              </TableCell>
              <TableCell
                className="table-cell"
                sx={{ fontSize: "15px", fontWeight: 800 }}
              >
                Amt
              </TableCell>
              <TableCell
                className="table-cell"
                sx={{ fontSize: "15px", fontWeight: 800 }}
              >
                Ccy
              </TableCell>
              <TableCell
                className="table-cell"
                sx={{ fontSize: "15px", fontWeight: 800 }}
              >
                Date Process in System
              </TableCell>
              <TableCell
                className="table-cell"
                sx={{ fontSize: "15px", fontWeight: 800 }}
              >
                WF Status
              </TableCell>
              <TableCell
                className="table-cell"
                sx={{ fontSize: "15px", fontWeight: 800 }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody className="table-body">
            {history.map((row, index) => (
              <TableRow key={index} className="table-row">
                <TableCell className="table-cell">
                  {customDateFormat(row?.new_allocation_date)}
                </TableCell>
                <TableCell className="table-cell">{row?.ca_change}</TableCell>
                <TableCell className="table-cell">{row?.ca_impact}</TableCell>
                <TableCell className="table-cell">{numberFormatter(row?.ca_amt ?? 0)}</TableCell>
                <TableCell className="table-cell">{row?.ca_curr}</TableCell>
                <TableCell className="table-cell" sx={{ textAlign: "center" }}>
                  {customDateFormat(row?.process_date)}
                </TableCell>
                <TableCell className="table-cell">
                  {row?.workflow?.workflow_status}
                </TableCell>
                <TableCell className="table-cell">
                  <IconButton>
                    <SettingsTwoToneIcon
                      onClick={() => {
                        setCurrentWorkflowName(
                          row?.workflow?.workflow_json_data?.workflow_name ?? ""
                        );
                        setCurrentTxnWorkflowDetails(row?.workflow ?? {});
                        setOpenUpdateGenericCrossAllocationWorkflowDialog(true);
                      }}
                      sx={{ fontSize: "25px", color: "#3b3f44" }}
                    />
                  </IconButton>
                  <IconButton>
                    <FileDownloadOutlinedIcon
                      sx={{ fontSize: "25px", color: "blue" }}
                    />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {openUpdateGenericCrossAllocationWorkflowDialog && (
        <UpdateGenericWorkflowDialog
          openUpdateGenericWorkflowDialog={
            openUpdateGenericCrossAllocationWorkflowDialog
          }
          handleUpdateGenericWorkflowDialog={handleUpdateGenericWorkflowDialog}
          workflowName={currentWorkflowName}
          currentWorkflowDetails={currentTxnWorkflowDetails}
        />
      )}
    </div>
  );
};

const CrossAllocation = ({ allocation, handleClose, isAuthorizedUser, viaHistoryLink = false }) => {
  const { history, formData, setFormData, submit, isApiInProgress, loadHistory} =
    useCrossAllocation(allocation);
  const [isDialogOpen, setIsDialogOpen] = useState(true);
  const [showHistory, setShowHistory] = useState(true);

  const fixedDetails = useMemo(
    () => [
      {
        label: "Accounting Month",
        value: allocation?.accounting_monthyear
          ? acctMonthToLabel(allocation?.accounting_monthyear)
          : "",
      },
      {
        label: "Txn Date",
        value: customDateFormat(allocation?.bank_txn?.Payment_Receive_Date),
      },
      {
        label: "System",
        value: allocation?.system_id,
      },

      {
        label: "Bank A/c#",
        value: allocation?.bank_txn?.bank_details?.account_number,
      },
      {
        label: "Status",
        value: allocation?.allocation_status,
      },
      {
        label: "Receivable Amt",
        value: numberFormatter(allocation?.bank_txn?.Receivable_Amount ?? 0),
      },
    ],
    [allocation]
  );

  useEffect(() => {
    setShowHistory((!!history.length) || !isAuthorizedUser || viaHistoryLink);
  }, [history?.length]);

  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
    handleClose("");
  }, [setIsDialogOpen]);

  const handleSubmitAssing = useCallback(
    async (e) => {
      e.preventDefault();
      const submitResponse = await submit();
      if(submitResponse && ("shouldShowHistory" in submitResponse) && submitResponse.shouldShowHistory) {
        setShowHistory(true);
        setFormData({
          aprover_name: allocation?.aprover_name || "",
          request_date: getTodaysDateInLocalTimezone()
        });
        loadHistory()
      }
    },
    [setIsDialogOpen, submit]
  );

  const handleInputChange = useCallback(
    (e) => {
      setFormData((data) => ({
        ...data,
        [e.target.name]: e.target.value,
      }));
    },
    [setFormData]
  );

    const handleAddShowHistory=()=>{
    if(!isAuthorizedUser && showHistory){
      toast.error("You are not assigned user!! Please re-assign and then try.", {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 1000,
      });
      return;
    }
    else{
      setShowHistory(!showHistory);
    }
  }

  return (
    <Dialog
      open={isDialogOpen}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') {
          return;
        }
        handleCloseDialog();
      }}
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          maxWidth: "800px",
        },
      }}
    >
      <ToastContainer />
      <form onSubmit={handleSubmitAssing}>
        <DialogTitle
          style={{
            background: "#FCFCFC",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: "50px",
          }}
        >
          <Typography sx={{ fontSize: 20 }}> Cross Allocation</Typography>
          <Typography
            sx={{ fontSize: 15, fontWeight: 750, marginLeft: "30px" }}
          >
            Policy#: {allocation?.policy_id}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {showHistory ? (
            <History fixedDetails={fixedDetails} history={history} />
          ) : (
            <>
              <Grid container>
                {fixedDetails?.map(({ label, value }, i) => (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    key={label}
                    paddingLeft={i % 2 ? 2 : 0}
                    paddingRight={i % 2 ? 0 : 2}
                    marginBottom={2}
                  >
                    <span>{label} </span>
                    <span style={{ fontWeight: "bold" }}>: {value}</span>
                  </Grid>
                ))}
              </Grid>

              <Grid container>
                <Grid item xs={12} md={6} paddingRight={2}>
                  <p style={{ fontSize: 13, color: "#17191B" }}>Amount</p>

                  <Input
                    style={{ ...inputStyleObj }}
                    placeholder="Enter amount here"
                    name="ca_amt"
                    value={formData?.ca_amt}
                    required
                    onChange={(e) => {
                      if (isNaN(e.target.value)) {
                        e.preventDefault();
                        e.stopPropagation();
                        e.target.value = formData.ca_amt || "";
                        return;
                      }
                      handleInputChange(e);
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6} paddingLeft={2}>
                  <p style={{ fontSize: 13, color: "#17191B" }}>What Changed</p>

                  <TextareaAutosize
                    style={{
                      height: 50,
                      width: "100%",
                    }}
                    name="ca_change"
                    value={formData?.ca_change}
                    onChange={handleInputChange}
                    placeholder="Write details here"
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6} paddingRight={2}>
                  <p style={{ fontSize: 13, color: "#17191B" }}>
                    Impact to Prior Allocation
                  </p>

                  <Input
                    style={{ ...inputStyleObj }}
                    placeholder="Write details here"
                    name="ca_impact"
                    value={formData?.ca_impact}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6} paddingLeft={2}>
                  <p style={{ fontSize: 13, color: "#17191B" }}>
                    Date Process in System
                  </p>

                  <Input
                    type="date"
                    style={{ ...inputStyleObj }}
                    name="process_date"
                    value={formData?.process_date}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6} paddingRight={2}>
                  <p style={{ fontSize: 13, color: "#17191B" }}>
                    New Allocation Date
                  </p>

                  <Input
                    type="date"
                    style={{ ...inputStyleObj }}
                    name="new_allocation_date"
                    value={formData?.new_allocation_date}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions style={{ justifyContent: "flex-end" }}>
          { !viaHistoryLink &&
          <Button
            onClick={() => handleAddShowHistory()}
            color={showHistory ? "warning" : "inherit"}
            variant={showHistory ? "contained" : "outlined"}
            sx={{ borderRadius: 100 }}
          >
            {showHistory ? "Add" : "History"}
          </Button>
          }
          <Button
            onClick={handleCloseDialog}
            color="inherit"
            variant="outlined"
            sx={{ borderRadius: 100, border: "1px solid #BCBDBF" }}
          >
            Close
          </Button>
          {!showHistory && (
            <Button
              variant="contained"
              color="warning"
              sx={{ borderRadius: 100 }}
              type={submit}
              disabled ={isApiInProgress}
            >
              Submit
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CrossAllocation;
